import HubLeadCTA from "@/components/HubLeadCTA";
import NaatiClusterLinks from "@/components/NaatiClusterLinks";
// r24 Wave 2 — NAATI TH provinces hub index (EN mirror)
import { createFileRoute, Link } from "@tanstack/react-router";
import { NAATI_PROVINCES } from "@/data/naati-th-provinces";
import { canonicalAndHreflangEn, ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";
const REGION_EN_ORDER = [
  "Bangkok & Vicinity", "North", "Northeast (Isaan)", "Central",
  "East", "West", "South",
] as const;

export const Route = createFileRoute("/en/translate/naati/city/")({
  head: () => {
    const url = `${SITE}/en/translate/naati/city`;
    const title = `NAATI Translation Across All 77 Thai Provinces — EMS/DHL to Every District`;
    const desc = `NAATI Certified translation covering all ${NAATI_PROVINCES.length} Thai provinces — head office in Bangkok Lat Phrao, EMS in-country 1-2 business days, upcountry clients simply scan the original by LINE. Message us for a same-day answer.`;
    return {
      meta: [
        { title: ctaTitle(title, "/en/translate/naati/city/index", "en") }, { name: "description", content: ctaDesc(desc, "/en/translate/naati/city/index", "en")},
        { property: "og:title", content: ctaTitle(title, "/en/translate/naati/city/index", "en")}, { property: "og:description", content: ctaDesc(desc, "/en/translate/naati/city/index", "en")},
        { property: "og:url", content: url }, { property: "og:locale", content: "en_US" },
      ],
      links: canonicalAndHreflangEn("/en/translate/naati/city", "/translate/naati/city"),
      scripts: [
        { type: "application/ld+json", children: JSON.stringify({
          "@context": "https://schema.org", "@type": "ItemList", inLanguage: "en",
          numberOfItems: NAATI_PROVINCES.length,
          itemListElement: NAATI_PROVINCES.map((p, i) => ({
            "@type": "ListItem", position: i + 1, name: `NAATI Translation ${p.en}`,
            url: `${SITE}/en/translate/naati/city/${p.slug}`,
          })),
        }) },
        { type: "application/ld+json", children: JSON.stringify({
          "@context": "https://schema.org", "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/en` },
            { "@type": "ListItem", position: 2, name: "NAATI Translation", item: `${SITE}/en/translate/naati` },
            { "@type": "ListItem", position: 3, name: "All 77 Provinces", item: url },
          ],
        }) },
      ],
    };
  },
  component: CityHubEn,
});

function CityHubEn() {
  return (
    <main className="mx-auto max-w-5xl px-4 py-10">
      <nav className="text-sm text-slate-500 mb-4" aria-label="Breadcrumb">
        <Link to="/en" className="hover:underline">Home</Link> ·{" "}
        <Link to="/en/translate/naati" className="hover:underline">NAATI Translation</Link> ·{" "}
        <span className="text-slate-700">All 77 Provinces</span>
      </nav>
      <h1 className="text-3xl md:text-4xl font-bold text-navy-deep mb-3">NAATI Translation Across All {NAATI_PROVINCES.length} Thai Provinces</h1>
      <p data-speakable="true" className="text-lg text-slate-700 mb-8 direct-answer">
        Upcountry clients order NAATI translation from any province — scan the original by LINE for our review, our
        CPN-holding translators stamp at the Lat Phrao office, and EMS/Kerry delivers the hard copy within 1-2 business days.
      </p>
      {REGION_EN_ORDER.map((region) => {
        const provinces = NAATI_PROVINCES.filter((p) => p.regionEn === region);
        if (provinces.length === 0) return null;
        return (
          <section key={region} className="mb-10">
            <h2 className="text-2xl font-semibold text-navy-deep border-b border-gold pb-2 mb-4">
              {region} <span className="text-sm font-normal text-slate-500">({provinces.length} provinces)</span>
            </h2>
            <ul className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {provinces.map((p) => (
                <li key={p.slug}>
                  <Link
                    to="/en/translate/naati/city/$slug" params={{ slug: p.slug }}
                    className="block border rounded-md p-2 text-sm hover:border-gold hover:bg-amber-50/50 transition"
                  >
                    <span className="font-medium text-navy-deep">{p.en}</span>
                    <span className="block text-xs text-slate-500">{p.th}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
      <NaatiClusterLinks />
    <HubLeadCTA locale="en" service="NAATI" />
      </main>
  );
}
